import { useState, useEffect } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, WhatsappLogo, X } from "@phosphor-icons/react";

const MODULES = ["lead", "quotation", "invoice", "collection", "purchase", "vendor", "delivery", "service", "amc", "hr"];
const RECEIVER_TYPES = ["customer", "vendor", "staff"];

const WhatsAppTemplates = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [moduleFilter, setModuleFilter] = useState("");
  const [editor, setEditor] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/templates${moduleFilter ? `?module=${moduleFilter}` : ""}`);
      setItems(r.data || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleFilter]);

  const save = async () => {
    if (!editor.module || !editor.action || !editor.name || !editor.body) { toast.error("All fields required"); return; }
    try {
      if (editor.template_id) {
        await axios.put(`${API}/whatsapp/templates/${editor.template_id}?requester_role=${user?.role || ""}`, editor);
      } else {
        await axios.post(`${API}/whatsapp/templates?requester_role=${user?.role || ""}`, editor);
      }
      toast.success("Saved"); setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template ${t.name}?`)) return;
    try { await axios.delete(`${API}/whatsapp/templates/${t.template_id}?requester_role=${user?.role || ""}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">WhatsApp</div>
            <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Template Library</h1><HelpButton moduleKey="whatsapp_templates" /></div>
            <p className="text-base text-slate-700 mt-1">{items.length} templates · variables auto-rendered at send time</p>
          </div>
          <button data-testid="tpl-new-btn" onClick={() => setEditor({ module: "lead", action: "", name: "", receiver_type: "customer", body: "", is_active: true })} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2"><Plus size={16} weight="bold" /> New Template</button>
        </div>

        <div className="bg-white border border-slate-200 p-4 mb-4">
          <select data-testid="tpl-filter-module" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="rounded-none border border-slate-300 p-2 outline-none text-sm">
            <option value="">All modules</option>
            {MODULES.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t, i) => (
            <div key={t.template_id} data-testid={`tpl-card-${i}`} className="bg-white border border-slate-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">{t.module} · {t.action}</div>
                  <h3 className="font-semibold text-slate-900 mt-0.5">{t.name}</h3>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">→ {t.receiver_type}</div>
                </div>
                <div className="flex items-center gap-1">
                  {t.is_system && <span className="text-[9px] uppercase tracking-wider font-bold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5">SYS</span>}
                  <button onClick={() => setEditor(t)} className="p-1 hover:bg-slate-100"><PencilSimple size={12} /></button>
                  <button onClick={() => remove(t)} className="p-1 text-rose-700 hover:bg-rose-50"><Trash size={12} /></button>
                </div>
              </div>
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono bg-slate-50 p-2 max-h-32 overflow-y-auto">{t.body}</pre>
            </div>
          ))}
        </div>
      </div>

      {editor && (
        <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center p-4" data-testid="tpl-editor">
          <div className="bg-white border border-slate-200 w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="border-b px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><WhatsappLogo size={18} className="text-emerald-600" /><h3 className="font-semibold">{editor.template_id ? "Edit" : "New"} Template</h3></div>
              <button onClick={() => setEditor(null)} className="p-2 hover:bg-slate-100"><X size={14} /></button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Module *</label>
                  <select data-testid="tpl-fld-module" value={editor.module} onChange={e => setEditor({...editor, module: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    {MODULES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Receiver *</label>
                  <select data-testid="tpl-fld-receiver" value={editor.receiver_type} onChange={e => setEditor({...editor, receiver_type: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    {RECEIVER_TYPES.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Action key *</label>
                  <input data-testid="tpl-fld-action" value={editor.action || ""} onChange={e => setEditor({...editor, action: e.target.value.replace(/\s+/g, "_").toLowerCase()})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none font-mono" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Display name *</label>
                <input data-testid="tpl-fld-name" value={editor.name || ""} onChange={e => setEditor({...editor, name: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Message body *</label>
                <textarea data-testid="tpl-fld-body" value={editor.body || ""} onChange={e => setEditor({...editor, body: e.target.value})} rows={10} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none font-mono leading-relaxed" />
                <div className="text-[10px] text-slate-500 mt-1">
                  Available variables: <code>{"{name}, {sender_name}, {company_name}, {doc_number}, {amount}, {expiry_date}, {quotation_pdf_link}, {invoice_pdf_link}, {receipt_pdf_link}, {po_pdf_link}, {delivery_challan_pdf_link}, {installation_completion_pdf_link}, {customer_statement_pdf_link}"}</code>
                </div>
              </div>
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => setEditor(null)} className="rounded-none border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100">Cancel</button>
              <button data-testid="tpl-save" onClick={save} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplates;
